import { Navigate } from "react-router";
import * as Dtos from "../dtos/Tmd.dtos";
import type { AppRouteObject, RouteMetadataContext } from "./types";
import { lazyRoute } from "./lazyRoute";
import { FontAwesomeIcons } from "src/constants/fontAwesomeIcons";

const committeeTopicPermissions: Record<string, Dtos.Permission | undefined> = {
    committees: Dtos.Permission.CommitteeView,
    committee: Dtos.Permission.CommitteeView,
    "committee-meetings": Dtos.Permission.CommitteeMeetingView,
    "committee-meeting": Dtos.Permission.CommitteeMeetingView,
    "committee-meeting-attendances": Dtos.Permission.CommitteeMeetingAttendanceView,
    "committee-meeting-attendance": Dtos.Permission.CommitteeMeetingAttendanceView,
    "committee-members": Dtos.Permission.CommitteeMemberView,
    "committee-member": Dtos.Permission.CommitteeMemberView,
    "committee-member-roles": Dtos.Permission.CommitteeMemberRoleView,
    "committee-member-role": Dtos.Permission.CommitteeMemberRoleView,
    "committee-regular-invitees": Dtos.Permission.CommitteeRegularInviteeView,
    "committee-regular-invitee": Dtos.Permission.CommitteeRegularInviteeView,
};

const humanize = (path: string): string => path
    .replace(/^:/, "")
    .replace(/[-_]/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, character => character.toUpperCase());

const routeValue = (path: string, context: RouteMetadataContext): string => path.replace(/:([A-Za-z0-9_]+)/g, (_match, key: string) => context.params[key] ?? `:${key}`);

const committeeLabel = (path: string, context: RouteMetadataContext): string => {
    if (path === ":committeeId") return context.labels.committeeName ?? "Committee";
    if (path === ":committeeMeetingId") return context.labels.committeeMeetingName ?? "Meeting";
    if (path === ":committeeMeetingAttendanceId") return context.labels.committeeMeetingAttendanceName ?? "Attendance";
    if (path === ":committeeMemberId") return context.labels.committeeMemberName ?? "Member";
    if (path === ":committeeMemberRoleId") return context.labels.committeeMemberRoleName ?? "Role";
    if (path === ":committeeRegularInviteeId") return context.labels.committeeRegularInviteeName ?? "Regular Invitee";
    if (path === "create") return "Create";
    return humanize(path);
};

const committeeBreadcrumbIcon = (path: string): typeof FontAwesomeIcons.Duotone.HOME | undefined => {
    if (path === ":committeeId") return FontAwesomeIcons.Duotone.USERSVIEWFINDER;
    if (path === ":committeeMeetingId") return FontAwesomeIcons.Duotone.BOOTHCURTAIN;
    if (path === ":committeeMeetingAttendanceId") return FontAwesomeIcons.Duotone.CLIPBOARDUSER;
    if (path === ":committeeMemberId") return FontAwesomeIcons.Duotone.USER;
    if (path === ":committeeMemberRoleId") return FontAwesomeIcons.Duotone.USERSHIELD;
    if (path === ":committeeRegularInviteeId") return FontAwesomeIcons.Duotone.CALENDARCIRCLEUSER;
    if (path === "details") return FontAwesomeIcons.Duotone.CIRCLEINFO;
    if (path === "meetings") return FontAwesomeIcons.Duotone.HANDSHAKE;
    if (path === "members") return FontAwesomeIcons.Duotone.USER;
    if (path === "roles") return FontAwesomeIcons.Duotone.USERSHIELD;
    if (path === "regularinvitees") return FontAwesomeIcons.Duotone.CALENDARCIRCLEUSER;
    return undefined;
};

const decorateCommitteeRoutes = (routes: AppRouteObject[], parentPath = ""): AppRouteObject[] => routes.map(route => {
    const path = route.path;
    const fullPath = path ? `${parentPath}/${path}`.replace(/\/+/g, "/") : parentPath;
    const topicKey = route.handle?.help?.topicKey;
    const title = route.handle?.title ?? (path ? ((path.startsWith(":") || path === "details" || path === "create")
        ? ((context: RouteMetadataContext) => committeeLabel(path, context))
        : humanize(path)) : undefined);
    const accessPermission = topicKey ? committeeTopicPermissions[topicKey] : undefined;
    const access = route.handle?.access !== undefined
        ? route.handle.access
        : accessPermission === undefined
            ? null
            : { permission: accessPermission };
    const breadcrumb = route.handle?.breadcrumb !== undefined
        ? route.handle.breadcrumb
        : !path
            ? null
            : {
                label: (context: RouteMetadataContext) => committeeLabel(path, context),
                icon: committeeBreadcrumbIcon(path),
                include: !route.index,
                url: (context: RouteMetadataContext) => {
                    const resolved = routeValue(fullPath, context);
                    return path.startsWith(":") ? `${resolved}/details` : resolved;
                },
            };
    const navigation = route.handle?.navigation;
    return {
        ...route,
        handle: { ...route.handle, title, access, breadcrumb, navigation },
        children: route.children ? decorateCommitteeRoutes(route.children, fullPath) : undefined,
    } as AppRouteObject;
});

const rawCommitteeRoutes = {
    path: "committees",
    handle: {
        title: "Committee",
        help: null,
        breadcrumb: { label: "Committees", icon: FontAwesomeIcons.Duotone.COMMITTEE, url: "/committees" },
    },
    children: [
        {
            index: true,
            lazy: lazyRoute(() => import("../containers/Committees/Committees/CommitteesPage")),
            handle: { help: { topicKey: "committees" } }
        },
        {
            path: "create",
            lazy: lazyRoute(() => import("../containers/Committees/Committee/CommitteePage")),
            handle: { help: { topicKey: "committee" } }
        },
        {
            path: ":committeeId",
            handle: { help: null },
            children: [
                {
                    index: true,
                    element: <Navigate replace to="details" />,
                    handle: { help: null }
                },
                {
                    path: "details",
                    lazy: lazyRoute(() => import("../containers/Committees/Committee/CommitteePage")),
                    handle: { help: { topicKey: "committee" } }
                },
                {
                    path: "meetings",
                    handle: { help: null },
                    children: [
                        {
                            index: true,
                            lazy: lazyRoute(() => import("../containers/Committees/CommitteeMeetings/CommitteeMeetingsPage")),
                            handle: { help: { topicKey: "committee-meetings" } }
                        },
                        {
                            path: "create",
                            lazy: lazyRoute(() => import("../containers/Committees/CommitteeMeeting/CommitteeMeetingPage")),
                            handle: { help: { topicKey: "committee-meeting" } }
                        },
                        {
                            path: ":committeeMeetingId",
                            handle: { help: null },
                            children: [
                                {
                                    index: true,
                                    element: (
                                        <Navigate replace to="details" />
                                    ),
                                    handle: { help: null }
                                },
                                {
                                    path: "details",
                                    lazy: lazyRoute(() => import("../containers/Committees/CommitteeMeeting/CommitteeMeetingPage")),
                                    handle: { help: { topicKey: "committee-meeting" } }
                                },
                                {
                                    path: "attendances",
                                    handle: { help: null },
                                    children: [
                                        {
                                            index: true,
                                            lazy: lazyRoute(() => import("../containers/Committees/CommitteeMeetingAttendances/CommitteeMeetingAttendancesPage")),
                                            handle: { help: { topicKey: "committee-meeting-attendances" } }
                                        },
                                        {
                                            path: "create",
                                            lazy: lazyRoute(() => import("../containers/Committees/CommitteeMeetingAttendance/CommitteeMeetingAttendancePage")),
                                            handle: { help: { topicKey: "committee-meeting-attendance" } }
                                        },
                                        {
                                            path: ":committeeMeetingAttendanceId",
                                            handle: { help: null },
                                            children: [
                                                {
                                                    index: true,
                                                    element: (
                                                        <Navigate replace to="details" />
                                                    ),
                                                    handle: { help: null }
                                                },
                                                {
                                                    path: "details",
                                                    lazy: lazyRoute(() => import("../containers/Committees/CommitteeMeetingAttendance/CommitteeMeetingAttendancePage")),
                                                    handle: { help: { topicKey: "committee-meeting-attendance" } }
                                                },
                                            ],
                                        },
                                    ],
                                },
                            ],
                        },
                    ],
                },
                {
                    path: "members",
                    handle: { help: null },
                    children: [
                        {
                            index: true,
                            lazy: lazyRoute(() => import("../containers/Committees/CommitteeMembers/CommitteeMembersPage")),
                            handle: { help: { topicKey: "committee-members" } }
                        },
                        {
                            path: "create",
                            lazy: lazyRoute(() => import("../containers/Committees/CommitteeMember/CommitteeMemberPage")),
                            handle: { help: { topicKey: "committee-member" } }
                        },
                        {
                            path: ":committeeMemberId",
                            handle: { help: null },
                            children: [
                                {
                                    index: true,
                                    element: (
                                        <Navigate replace to="details" />
                                    ),
                                    handle: { help: null }
                                },
                                {
                                    path: "details",
                                    lazy: lazyRoute(() => import("../containers/Committees/CommitteeMember/CommitteeMemberPage")),
                                    handle: { help: { topicKey: "committee-member" } }
                                },
                                {
                                    path: "roles",
                                    handle: { help: null },
                                    children: [
                                        {
                                            index: true,
                                            lazy: lazyRoute(() => import("../containers/Committees/CommitteeMemberRoles/CommitteeMemberRolesPage")),
                                            handle: { help: { topicKey: "committee-member-roles" } }
                                        },
                                        {
                                            path: "create",
                                            lazy: lazyRoute(() => import("../containers/Committees/CommitteeMemberRole/CommitteeMemberRolePage")),
                                            handle: { help: { topicKey: "committee-member-role" } }
                                        },
                                        {
                                            path: ":committeeMemberRoleId",
                                            handle: { help: null },
                                            children: [
                                                {
                                                    index: true,
                                                    element: (
                                                        <Navigate replace to="details" />
                                                    ),
                                                    handle: { help: null }
                                                },
                                                {
                                                    path: "details",
                                                    lazy: lazyRoute(() => import("../containers/Committees/CommitteeMemberRole/CommitteeMemberRolePage")),
                                                    handle: { help: { topicKey: "committee-member-role" } }
                                                },
                                            ],
                                        },
                                    ],
                                },
                            ],
                        },
                    ],
                },
                {
                    path: "regularinvitees",
                    handle: { help: null },
                    children: [
                        {
                            index: true,
                            lazy: lazyRoute(() => import("../containers/Committees/CommitteeRegularInvitees/CommitteeRegularInviteesPage")),
                            handle: { help: { topicKey: "committee-regular-invitees" } }
                        },
                        {
                            path: "create",
                            lazy: lazyRoute(() => import("../containers/Committees/CommitteeRegularInvitee/CommitteeRegularInviteePage")),
                            handle: { help: { topicKey: "committee-regular-invitee" } }
                        },
                        {
                            path: ":committeeRegularInviteeId",
                            handle: { help: null },
                            children: [
                                {
                                    index: true,
                                    element: (
                                        <Navigate replace to="details" />
                                    ),
                                    handle: { help: null }
                                },
                                {
                                    path: "details",
                                    lazy: lazyRoute(() => import("../containers/Committees/CommitteeRegularInvitee/CommitteeRegularInviteePage")),
                                    handle: { help: { topicKey: "committee-regular-invitee" } }
                                },
                            ],
                        },
                    ],
                },
            ],
        },
    ],
} satisfies AppRouteObject;

export const committeeRoutes = decorateCommitteeRoutes([rawCommitteeRoutes])[0];

